import React, { useEffect, useRef, useState } from 'react';
import { FiMessageCircle, FiSend, FiX } from 'react-icons/fi';
import { useShop } from '../contexts/ShopContext';
import { useLanguage } from '../i18n';
import { askChatbot, getChatbotConfig } from '../api';

/**
 * Floating shop chat. Replies come from the owner Q&A list and/or Gemini and
 * are always scoped to THIS shop. The colour follows the shop theme unless the
 * owner picked a custom colour in the dashboard.
 */
export default function ChatbotWidget() {
  const { shop } = useShop();
  const { t, lang } = useLanguage();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const listRef = useRef(null);

  useEffect(() => {
    if (!shop || !shop.id) return;
    getChatbotConfig(shop.id)
      .then((c) => {
        setCfg(c);
        if (c && c.enabled && (c.welcome || '').trim()) {
          setMessages([{ role: 'bot', text: c.welcome }]);
        }
      })
      .catch(() => setCfg(null));
  }, [shop && shop.id]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, busy]);

  if (!cfg || !cfg.enabled) return null;

  const brand = ((cfg.color || '').trim()) || 'var(--primary)';
  const soft = { backgroundColor: brand, color: '#fff' };

  const sendText = async (raw) => {
    const text = (raw || '').trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const r = await askChatbot({ shop_id: shop.id, message: text, lang });
      const reply = ((r && r.reply) || '').trim() || t('chatbotError');
      setMessages((m) => [...m, { role: 'bot', text: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: t('chatbotError') }]);
    } finally {
      setBusy(false);
    }
  };

  const questions = (cfg.questions || []).filter(Boolean);
  const showQuestions = questions.length > 0 && messages.filter((m) => m.role === 'user').length === 0;

  return (
    <>
      {open && (
        <div className="anim-pop fixed z-[70] bottom-36 md:bottom-24 left-4 w-[330px] max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border dark:border-gray-700 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3" style={soft}>
            <div className="flex items-center gap-2 text-sm font-semibold truncate">
              <FiMessageCircle className="w-4 h-4" />
              {t('chatbot')}
            </div>
            <button onClick={() => setOpen(false)} className="text-white/90 hover:text-white transition-transform active:scale-90" title="Close">
              <FiX className="w-4 h-4" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50 dark:bg-gray-900" style={{ maxHeight: '55vh' }}>
            {messages.map((m, i) => (
              <div key={i} className={`anim-bubble flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={m.role === 'user'
                    ? 'text-sm rounded-2xl rounded-br-sm px-3 py-2 max-w-[80%] whitespace-pre-wrap shadow-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 text-sm rounded-2xl rounded-bl-sm px-3 py-2 max-w-[80%] border dark:border-gray-700 whitespace-pre-wrap shadow-sm'}
                  style={m.role === 'user' ? soft : undefined}
                >
                  {m.text}
                </div>
              </div>
            ))}

            {/* Suggested questions the owner added - click to see the answer */}
            {showQuestions && (
              <div className="anim-fade-up space-y-2 pt-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {t('chatbotSuggest')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {questions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendText(q)}
                      className="text-left text-xs px-3 py-2 rounded-full border bg-white dark:bg-gray-800 dark:border-gray-700 hover:shadow-md active:scale-95 transition-all"
                      style={{ borderColor: brand, color: brand }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {busy && <div className="text-xs text-gray-400 anim-fade-in">{t('chatbotThinking')}</div>}
          </div>

          <div className="flex items-center gap-2 p-2 border-t dark:border-gray-700 bg-white dark:bg-gray-800">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendText(input)}
              placeholder={t('chatbotPlaceholder')}
              className="flex-1 text-sm px-3 py-2 rounded-lg border dark:border-gray-700 dark:bg-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
            />
            <button
              onClick={() => sendText(input)}
              disabled={busy || !input.trim()}
              className="p-2 rounded-lg text-white disabled:opacity-50 transition-transform active:scale-90 hover:brightness-110"
              style={soft}
              title={t('chatbotSend')}
            >
              <FiSend className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {!open && (
        <button
          onClick={() => setOpen(true)}
          title={t('chatbot')}
          className="anim-float-in fixed z-[70] bottom-36 md:bottom-24 left-4 inline-flex items-center gap-2 text-white text-xs md:text-sm font-bold px-4 py-3 rounded-full shadow-2xl hover:shadow-[0_10px_30px_rgba(0,0,0,.35)] hover:-translate-y-0.5 active:scale-95 transition-all"
          style={soft}
        >
          <FiMessageCircle className="w-4 h-4" />
          <span className="hidden sm:inline">{t('chatbot')}</span>
        </button>
      )}
    </>
  );
}

